import React from "react";
import { useTranslation } from "react-i18next";
import { Shield, Lock, Globe } from "lucide-react";
import { COMPANY } from "@/lib/company";
import { LocalizedLink } from "@/components/layout/LocalizedLink";

const footerLinks = {
  solutions: [
    { key: "governmentSolutions", href: "/government-solutions" },
    { key: "technologyPlatform", href: "/technology-platform" },
    { key: "operationalDelivery", href: "/operational-delivery" },
    { key: "globalPresence", href: "/global-presence" },
    { key: "tourismTrade", href: "/tourism-trade-support" },
  ],
  procurement: [
    { key: "procurement", href: "/procurement" },
    { key: "governance", href: "/governance" },
    { key: "implementation", href: "/implementation" },
    { key: "dataPrivacy", href: "/data-privacy" },
    { key: "security", href: "/security" },
  ],
  company: [
    { key: "about", href: "/about" },
    { key: "caseStudies", href: "/case-studies" },
    { key: "resources", href: "/resources" },
    { key: "faq", href: "/faq" },
    { key: "contact", href: "/contact" },
  ],
};

export const Footer = React.forwardRef<HTMLElement>(function Footer(_props, ref) {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer ref={ref} className="bg-primary text-primary-foreground">
      <div className="container-premium py-10 sm:py-12 lg:py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6 md:gap-8 lg:gap-6">
          {/* Brand */}
          <div className="md:col-span-2 lg:col-span-4">
            <LocalizedLink to="/" className="inline-flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 bg-primary-foreground/10 rounded-lg flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm font-display">VX</span>
              </div>
              <div>
                <span className="font-display font-semibold text-sm text-primary-foreground block leading-none">
                  {COMPANY.tradeName}
                </span>
                <span className="text-[8px] text-primary-foreground/70 font-medium uppercase tracking-widest">
                  {t('footer.governmentServices')}
                </span>
              </div>
            </LocalizedLink>
            <p className="text-primary-foreground/75 text-xs leading-relaxed max-w-xs mb-3">
              {t('footer.description')}
            </p>
            <p className="text-primary-foreground/60 text-xs mb-3">
              {COMPANY.legalName} · {COMPANY.taxIdLabel}: {COMPANY.taxId}
            </p>
            <div className="flex items-center gap-4 text-primary-foreground/70">
              <div className="flex items-center gap-1">
                <Shield className="h-3 w-3" />
                <span className="text-[10px] font-medium">{t('footer.gdpr')}</span>
              </div>
              <div className="flex items-center gap-1">
                <Lock className="h-3 w-3" />
                <span className="text-[10px] font-medium">{t('footer.secure')}</span>
              </div>
              <div className="flex items-center gap-1">
                <Globe className="h-3 w-3" />
                <span className="text-[10px] font-medium">{t('footer.global')}</span>
              </div>
            </div>
          </div>

          {/* Links - 3 columns */}
          <div className="md:col-span-2 lg:col-span-8 grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6">
            <div>
              <span className="text-primary-foreground font-medium text-xs mb-3 block">{t('footer.solutionsTitle')}</span>
              <ul className="space-y-2">
                {footerLinks.solutions.map((link) => (
                  <li key={link.href}>
                    <LocalizedLink to={link.href} className="text-primary-foreground/75 text-xs hover:text-primary-foreground transition-colors">
                      {t(`footer.links.${link.key}`)}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <span className="text-primary-foreground font-medium text-xs mb-3 block">{t('footer.procurementTitle')}</span>
              <ul className="space-y-2">
                {footerLinks.procurement.map((link) => (
                  <li key={link.href}>
                    <LocalizedLink to={link.href} className="text-primary-foreground/75 text-xs hover:text-primary-foreground transition-colors">
                      {t(`footer.links.${link.key}`)}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <span className="text-primary-foreground font-medium text-xs mb-3 block">{t('footer.companyTitle')}</span>
              <ul className="space-y-2">
                {footerLinks.company.map((link) => (
                  <li key={link.href}>
                    <LocalizedLink to={link.href} className="text-primary-foreground/75 text-xs hover:text-primary-foreground transition-colors">
                      {t(`footer.links.${link.key}`)}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-primary-foreground/10">
        <div className="container-premium py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-4 gap-y-1 text-[10px] text-primary-foreground/60">
              <span>© {currentYear} {COMPANY.tradeName}. {t('footer.allRightsReserved')}</span>
              <LocalizedLink to="/legal-notice" className="hover:text-primary-foreground/80 transition-colors">{t('footer.legalNotice')}</LocalizedLink>
              <LocalizedLink to="/privacy-policy" className="hover:text-primary-foreground/80 transition-colors">{t('footer.privacy')}</LocalizedLink>
              <LocalizedLink to="/terms" className="hover:text-primary-foreground/80 transition-colors">{t('footer.terms')}</LocalizedLink>
              <LocalizedLink to="/security" className="hover:text-primary-foreground/80 transition-colors">{t('footer.security')}</LocalizedLink>
              <LocalizedLink to="/faq" className="hover:text-primary-foreground/80 transition-colors">{t('footer.faq')}</LocalizedLink>
            </div>
            <p className="text-[10px] text-primary-foreground/60 text-center sm:text-right max-w-sm">
              {t('footer.boundary')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
});
